/**
 * FXIO - IntersectionObserver-based visibility class manager
 * ---------------------------------------------------------
 * Simpler and lighter than FXTrigger.
 * Adds/removes CSS classes when an element becomes visible or hidden.
 *
 * API:
 *   new FXIO({
 *     selector: ".fade",
 *     add: "is-visible",                // optional -> add when visible
 *     remove: "is-hidden",              // optional -> remove when visible
 *     imagesReadyClass: "images-ready", // optional -> added after animated img are ready
 *     rootMargin: "0px 0px -20% 0px"    // IO settings
 *   });
 */

export class FXIO {

    constructor(options) {

        this.selector = options.selector;

        this.addClass         = options.add || null;
        this.removeClass      = options.remove || null;
        this.imagesReadyClass = options.imagesReadyClass || null;
        this.imagesReadyDelay = options.imagesReadyDelay ?? 150;

        this.onVisible = options.onVisible || null;
        this.onHidden  = options.onHidden || null;

        this.threshold = options.threshold || 0.1;
        this.rootMargin = options.rootMargin || "0px";
        this.waitForDocumentInit = options.waitForDocumentInit || false;

        this.items = [...document.querySelectorAll(this.selector)];

        this.init();

    }

    init() {

        if (this.waitForDocumentInit && !document.documentElement.classList.contains('init')) {

            const observer = new MutationObserver(() => {

                if (!document.documentElement.classList.contains('init')) {

                    return;

                }

                observer.disconnect();
                this.observe();

            });

            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['class']
            });

            return;

        }

        this.observe();

    }

    observe() {

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {

                    const el = entry.target;

                    if (entry.isIntersecting) {
                        if (this.addClass) el.classList.add(this.addClass);
                        if (this.removeClass) el.classList.remove(this.removeClass);
                        if (this.onVisible) this.onVisible(el);
                        if (this.imagesReadyClass) this._waitForImages(el);
                    } else {

                        // Apply only when a remove class has been defined.
                        if (this.removeClass) {
                            if (this.addClass) el.classList.remove(this.addClass);
                            if (this.removeClass) el.classList.add(this.removeClass);
                        }

                        if (this.onHidden) this.onHidden(el);
                    }
                });
            },
            {
                threshold: this.threshold,
                rootMargin: this.rootMargin
            }
        );

        this.items.forEach(el => observer.observe(el));

    }

    _waitForImages(el) {

        const imgs = [...el.querySelectorAll('img[data-animation=image]')];

        if (imgs.length === 0) {
            el.classList.add(this.imagesReadyClass);
            return;
        }

        const promises = imgs.map(img => this._waitForRenderableImage(img));

        Promise.all(promises).then(() => {
            setTimeout(() => el.classList.add(this.imagesReadyClass), this.imagesReadyDelay);
        });

    }

    _waitForRenderableImage(img) {

        // A clipped image can delay lazy-loading in some browsers.
        // Force a real fetch before we wait for load/decode.
        if (img.loading === 'lazy') {
            img.loading = 'eager';
            if (img.srcset) img.srcset = img.srcset;
            else if (img.src) img.src = img.src;
        }

        return this._waitForImageLoad(img)
            .then(() => this._decodeImage(img))
            .catch(() => undefined);

    }

    _waitForImageLoad(img) {

        if (img.complete && img.naturalWidth > 0) {
            return Promise.resolve();
        }

        return new Promise(resolve => {

            const done = () => resolve();

            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });

        });

    }

    _decodeImage(img) {

        if (typeof img.decode !== 'function') {
            return Promise.resolve();
        }

        // decode() waits for the image candidate actually selected by the browser.
        return img.decode().catch(() => undefined);

    }

}
